import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Hls from 'hls.js'
import { eventsAPI } from '@/api/events'
import { authAPI } from '@/api/auth'
import { LiveEvent } from '@/types/event'
import ChatWindow from '@/components/chat/ChatWindow'
import PDFViewer from '@/components/pdf/PDFViewer'
import ReactionBar from '@/components/reactions/ReactionBar'
import ReactionDisplay from '@/components/reactions/ReactionDisplay'
import Loading from '@/components/common/Loading'
import { useReactions } from '@/hooks/useReactions'
// import { useAuth } from '@/contexts/AuthContext'
import PresentEffect from '@/components/presents/PresentEffect'
import { getWebSocketBaseUrl } from '@/utils/websocket'

function EventLive() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // const { user } = useAuth()
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)

  const [event, setEvent] = useState<LiveEvent | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showChat, setShowChat] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [presentEffect, setPresentEffect] = useState<{
    isVisible: boolean
    presentName: string
    presentImageUrl?: string
    customImageUrl?: string
    message?: string
    grantedByName: string
  }>({
    isVisible: false,
    presentName: '',
    grantedByName: ''
  })

  // リアクション機能のフック
  const { reactions, sendReaction } = useReactions(id || '')

  useEffect(() => {
    if (id) {
      loadEventAndStream()
    }

    return () => {
      // Cleanup on unmount
      if (hlsRef.current) {
        hlsRef.current.destroy()
      }
      if (id) {
        eventsAPI.leaveEvent(id).catch(console.error)
      }
    }
  }, [id])

  const loadEventAndStream = async () => {
    try {
      setIsLoading(true)

      // Load event details
      const eventData = await eventsAPI.getEvent(id!)
      setEvent(eventData)

      // テキストセミナーの場合はPDFを表示するため、ストリーミング設定をスキップ
      if (eventData.event_type === 'text_seminar') {
        console.log('Text seminar mode - PDF URL:', eventData.pdf_url)
        return
      }

      // Get stream info for video events
      const streamInfo = await eventsAPI.getStreamInfo(id!)

      // Setup HLS player for video streaming
      if (videoRef.current && streamInfo.url) {
        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 90,
          })

          hls.loadSource(streamInfo.url)
          hls.attachMedia(videoRef.current)

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            videoRef.current?.play().catch(console.error)
          })

          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (data.fatal) {
              switch(data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  console.error('Fatal network error encountered, trying to recover')
                  hls.startLoad()
                  break
                case Hls.ErrorTypes.MEDIA_ERROR:
                  console.error('Fatal media error encountered, trying to recover')
                  hls.recoverMediaError()
                  break
                default:
                  setError('ストリーミングエラーが発生しました')
                  break
              }
            }
          })

          hlsRef.current = hls
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
          // For Safari
          videoRef.current.src = streamInfo.url
          videoRef.current.addEventListener('loadedmetadata', () => {
            videoRef.current?.play().catch(console.error)
          })
        } else {
          setError('このブラウザはストリーミング再生に対応していません')
        }
      }
    } catch (err: any) {
      setError('ストリームの読み込みに失敗しました')
      console.error('Failed to load stream:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVolumeChange = (value: number) => {
    setVolume(value)
    if (videoRef.current) {
      videoRef.current.volume = value
      setIsMuted(value === 0)
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted
      videoRef.current.muted = newMuted
      setIsMuted(newMuted)
      if (!newMuted && volume === 0) {
        setVolume(1)
        videoRef.current.volume = 1
      }
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const handleExit = () => {
    if (id) {
      eventsAPI.leaveEvent(id).catch(console.error)
    }
    navigate(`/events/${id}`)
  }

  // プレゼント通知を処理する関数
  const handlePresentReceived = (data: any) => {
    console.log('🎁 Handling present notification:', data)
    setPresentEffect({
      isVisible: true,
      presentName: data.present_name || 'プレゼント',
      presentImageUrl: data.present_image_url,
      customImageUrl: data.custom_image_url,
      message: data.message,
      grantedByName: data.granted_by_name || data.granted_by_name || data.user_name || '送信者'
    })
  }

  // プレゼント通知専用のWebSocket接続
  useEffect(() => {
    // localStorageからトークンを直接取得
    const tokens = authAPI.getAuthTokens()
    console.log('🎁 Auth tokens:', tokens ? 'Found' : 'Not found')

    if (!tokens || !tokens.access_token) {
      console.log('🎁 No auth token available, skipping WebSocket connection')
      return
    }

    const wsBaseUrl = getWebSocketBaseUrl()
    const wsUrl = `${wsBaseUrl}/api/v1/ws/presents/notifications?token=${tokens.access_token}`
    console.log('🎁 Connecting to present notifications WebSocket:', wsUrl)

    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      console.log('🎁 Present notifications WebSocket connected')
    }

    ws.onmessage = (event) => {
      console.log('🎁 Present WebSocket message received:', event.data)
      try {
        const data = JSON.parse(event.data)
        console.log('🎁 Parsed present notification:', data)

        // プレゼント通知を受信 (event_type: "present_effect")
        if (data.event_type === 'present_effect' || data.present_id) {
          handlePresentReceived(data)
        }
      } catch (error) {
        console.error('Failed to parse present WebSocket message:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('🎁 Present WebSocket error:', error)
    }

    ws.onclose = () => {
      console.log('🎁 Present WebSocket disconnected')
    }

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, []) // 初回マウント時のみ実行

  if (isLoading) {
    return <Loading fullScreen message="ストリームを読み込んでいます..." />
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4">{error || 'イベントが見つかりません'}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-screen bg-black overflow-hidden">
      {/* Video Player or PDF Viewer */}
      <div className="relative h-full">
        {/* テキストセミナーの場合はPDFビューアを表示 */}
        {event?.event_type === 'text_seminar' ? (
          <div className="w-full h-full">
            {event.pdf_url ? (
              <PDFViewer pdfUrl={event.pdf_url} title={event.title} />
            ) : (
              <div className="flex items-center justify-center h-full bg-white text-gray-700">
                <div className="text-center">
                  <p className="text-2xl mb-3">📄 PDFドキュメントが設定されていません</p>
                  <p className="text-lg">主催者がドキュメントを準備するまでお待ちください</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ビデオストリーミングの場合 */
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            controls={false}
            playsInline
            muted={isMuted}
          />
        )}

        {/* Reaction Display Overlay */}
        <ReactionDisplay reactions={reactions} duration={3000} />

        {/* Controls Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4" style={{ zIndex: 65 }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Exit Button */}
              <button
                onClick={handleExit}
                className="text-white hover:text-gray-300"
              >
                ✕ 退出
              </button>

              {/* Volume Controls - ビデオイベントの場合のみ表示 */}
              {event?.event_type !== 'text_seminar' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleMute}
                    className="text-white hover:text-gray-300"
                  >
                    {isMuted ? '🔇' : '🔊'}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                    className="w-20"
                  />
                </div>
              )}

              {/* Event Title */}
              <div className="text-white">
                <h1 className="font-semibold">{event.title}</h1>
                <p className="text-sm text-gray-300">
                  {event.current_participants}人が視聴中
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Chat Toggle */}
              {event.chat_enabled && (
                <button
                  onClick={() => setShowChat(!showChat)}
                  className="text-white hover:text-gray-300 px-3 py-1 border border-white/50 rounded"
                >
                  💬 チャット
                </button>
              )}

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-gray-300"
              >
                {isFullscreen ? '⊡' : '⊞'}
              </button>
            </div>
          </div>

          {/* Reaction Bar - 中央下部に配置 - チャットパネルが開いている時は非表示 */}
          {!showChat && (
            <div className="flex justify-center mt-3" style={{ position: 'relative', zIndex: 55 }}>
              <ReactionBar
                eventId={id || ''}
                onReaction={sendReaction}
                disabled={!event}
              />
            </div>
          )}
        </div>
      </div>

      {/* Chat Panel - Bottom Overlay Position */}
      {showChat && event.chat_enabled && (
        <div className="absolute left-0 right-0 bg-white border-t-2 border-gray-300 shadow-2xl" style={{ bottom: '0', height: '400px', zIndex: 50 }}>
          <div className="absolute top-2 right-2 z-10">
            <button
              onClick={() => setShowChat(false)}
              className="text-gray-600 hover:text-gray-800 p-2 bg-white rounded-full shadow-md"
              title="チャットを閉じる"
            >
              ✕
            </button>
          </div>
          <ChatWindow eventId={id!} onPresentReceived={handlePresentReceived} />
        </div>
      )}

      {/* Present Effect Display - Three.js版 */}
      <PresentEffect
        isVisible={presentEffect.isVisible}
        presentName={presentEffect.presentName}
        presentImageUrl={presentEffect.presentImageUrl}
        customImageUrl={presentEffect.customImageUrl}
        message={presentEffect.message}
        grantedByName={presentEffect.grantedByName}
        onComplete={() => setPresentEffect({
          isVisible: false,
          presentName: '',
          grantedByName: ''
        })}
      />

      {/* 開発用: プレゼントエフェクトテストボタン */}
      {import.meta.env.DEV && (
        <div className="fixed top-20 left-4 z-50">
          <button
            onClick={() => {
              // テスト用のプレゼントデータをランダムに選択
              const testPresents = [
                {
                  presentName: 'ハート',
                  presentImageUrl: 'https://via.placeholder.com/300x300/FF69B4/FFFFFF?text=❤️',
                  message: '応援しています！',
                  grantedByName: 'テストユーザー'
                },
                {
                  presentName: 'ダイヤモンド',
                  presentImageUrl: 'https://via.placeholder.com/300x300/87CEEB/FFFFFF?text=💎',
                  message: '特別な贈り物です！',
                  grantedByName: 'VIPユーザー'
                },
                {
                  presentName: 'スター',
                  presentImageUrl: 'https://via.placeholder.com/300x300/FFD700/FFFFFF?text=⭐',
                  message: '輝いています！',
                  grantedByName: 'ファン'
                },
                {
                  presentName: 'ロケット',
                  presentImageUrl: 'https://via.placeholder.com/300x300/FF4500/FFFFFF?text=🚀',
                  message: '宇宙まで届け！',
                  grantedByName: 'サポーター'
                }
              ]

              const randomPresent = testPresents[Math.floor(Math.random() * testPresents.length)]

              console.log('🎁 Testing present effect:', randomPresent.presentName)
              console.log('エフェクトは3フェーズ連続で流れます:')
              console.log('1. flowing（流れ落ちる）→ 2. contracting（収束）→ 3. bursting（爆発＋画像表示）')

              setPresentEffect({
                isVisible: true,
                ...randomPresent,
                customImageUrl: '',
              })
            }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-6 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105"
            title="プレゼントエフェクトをテスト（3フェーズ連続アニメーション）"
          >
            🎁 テストエフェクト
            <span className="text-xs opacity-90">（3フェーズ連続）</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default EventLive